import React, { useEffect, useState } from "react";
import { Paper, Stack, Typography, Chip, Box } from "@mui/material";
import { apiCallDatingProfile } from "../../../../Core/Apicall";
import CommonShimmer from "../../../Common/CommonShimmer/CommonShimmer";
import { useLocation } from "react-router-dom";

interface PreferenceData {
  kidsIds: { _id: string; kids: string }[];
  drinkIds: { _id: string; drink: string }[];
  futureKidIds: { _id: string; futureKids: string }[];
  exerciseIds: { _id: string; exercise: string }[];
  manQualityIds: { _id: string; manQuality: string }[];
  nonBinaryQualityIds: { _id: string; nonBinaryQuality: string }[];
  womanQualityIds: { _id: string; womanQuality: string }[];
  qualityIds: { _id: string; quality: string }[];
  hobbiesIds: { _id: string; hobbies: string }[];
  relationIds: { _id: string; relation: string }[];
  smokeIds: { _id: string; smoke: string }[];
  ageGroupId: { _id: string; ageGroup: string }[];
  bodyTypeId: { _id: string; bodyType: string }[];
  hairColorId: { _id: string; hairColor: string }[];
  heightId: { _id: string; height: string }[];
  occupationId: { _id: string; occupation: string }[];
  pronounsId: { _id: string; pronouns: string }[];
}

const DatingPreferences: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [preferences, setPreferences] = useState<PreferenceData | null>(null);

  const location = useLocation();
  const userData = location.state.user;
  
  const fetchUserPreferences = async (userId: any) => {
    try {
      setLoading(true);
      const res = await apiCallDatingProfile({
        userId,
      });

      if (res?.data) {
        setPreferences(res.data);
      }
    } catch (error) {
      console.error("Error fetching preferences:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userData) {
      fetchUserPreferences(userData.userId ? userData.userId : userData._id);
    }
  }, [userData]);

  const renderChips = (items: any[], labelKey: string) => {
    return items?.length > 0 ? (
      <Stack direction="row" flexWrap="wrap" gap={1} mt={1}>
        {items.map((item) => (
          <Chip
            key={item._id}
            label={item[labelKey]}
            sx={{ bgcolor: "black", color: "white" }}
          />
        ))}
      </Stack>
    ) : (
      <Typography variant="body2" color="text.secondary">
        No preferences selected
      </Typography>
    );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: "16px",
        textAlign: "center",
        maxWidth: "100%",
        width: "100%",
        backgroundColor: "transparent",
        maxHeight: "500px",
        overflowY: "auto",
        "&::-webkit-scrollbar": {
          width: "6px",
        },
        "&::-webkit-scrollbar-track": {
          background: "#f1f1f1",
          borderRadius: "3px",
        },
        "&::-webkit-scrollbar-thumb": {
          background: "#888",
          borderRadius: "3px",
          "&:hover": {
            background: "#555",
          },
        },
      }}
    >
      {loading ? (
        <CommonShimmer />
      ) : (
        <div
          style={{
            display: "block",
            justifyContent: "left",
            width: "100%",
            gap: "30px",
            padding: "5px",
            flexWrap: "wrap",
          }}
        >
          <div>
            <Typography sx={{ textAlign: "left", mb: 1 }}>
              Current Kids
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
              {renderChips(preferences?.kidsIds || [], "kids")}
            </Box>
          </div>
          <div>
            <Typography sx={{ textAlign: "left", mb: 1 }}>
              Future Kids
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
              {renderChips(preferences?.futureKidIds || [], "futureKids")}
            </Box>
          </div>
          <div>
            <Typography sx={{ textAlign: "left", mb: 1 }}>Drinking</Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
              {renderChips(preferences?.drinkIds || [], "drink")}
            </Box>
          </div>
          <div>
            <Typography sx={{ textAlign: "left", mb: 1 }}>Smoking</Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
              {renderChips(preferences?.smokeIds || [], "smoke")}
            </Box>
          </div>
          <div>
            <Typography sx={{ textAlign: "left", mb: 1 }}>Hobby</Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
              {renderChips(preferences?.hobbiesIds || [], "hobbies")}
            </Box>
          </div>
          <div>
            <Typography sx={{ textAlign: "left", mb: 1 }}>Relation</Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
              {renderChips(preferences?.relationIds || [], "relation")}
            </Box>
          </div>

          {/* Education */}
          <div>
            <Typography sx={{ textAlign: "left", mb: 1 }}>Education</Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
              {renderChips(preferences?.exerciseIds || [], "exercise")}
            </Box>
          </div>
          <div>
            <Typography sx={{ textAlign: "left", mb: 1 }}>Age Group</Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
              {renderChips(preferences?.ageGroupId || [], "ageGroup")}
            </Box>
          </div>

          <div>
            <Typography sx={{ textAlign: "left", mb: 1 }}>Body Type</Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
              {renderChips(preferences?.bodyTypeId || [], "bodyType")}
            </Box>
          </div>

          <div>
            <Typography sx={{ textAlign: "left", mb: 1 }}>
              Hair Color
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
              {renderChips(preferences?.hairColorId || [], "hairColor")}
            </Box>
          </div>

          <div>
            <Typography sx={{ textAlign: "left", mb: 1 }}>Height</Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
              {renderChips(preferences?.heightId || [], "height")}
            </Box>
          </div>

          <div>
            <Typography sx={{ textAlign: "left", mb: 1 }}>
              Occupation
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
              {renderChips(preferences?.occupationId || [], "occupation")}
            </Box>
          </div>

          <div>
            <Typography sx={{ textAlign: "left", mb: 1 }}>Pronouns</Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
              {renderChips(preferences?.pronounsId || [], "pronouns")}
            </Box>
          </div>
        </div>
      )}
    </Paper>
  );
};

export default DatingPreferences;